import * as lodash from 'lodash'
import { CubePresenter, FlipIntent, Logger, Presenter } from 'wdc-cube'

import { SearchBoxPresenter, type TextualSearchRequest } from 'src/components/searchbox'
import { MainPresenter } from 'src/main/Main.presenter'
import { AppStorage } from 'src/utils/AppStorage'

import HandleSearchFilterAppliedCtx from './contexts/HandleSearchFilterAppliedCtx'
import { TheActingKeys } from './ta_keys'
import { FilterEntryScope, HeaderScope, OverlayScope, ScoreCardScope, TheActingScope } from './ta_scopes'
import { TheActingService } from './ta_service'
import {
    CompanySortItem,
    CompanySortModel,
    ProfessionalSortItem,
    ProfessionalSortModel,
    type CompanyRow,
    type FetchRequest,
    type ProfessionalExistsFilterField,
    type ProfessionalExistsFilterMode,
    type ProfessionalRow,
    type TablePage
} from './ta_types'

import ConfirmExportDataLimitPresenter from 'src/main/ConfirmExportDataLimit.presenter'
import { excludedExportDocIdsByType } from 'src/main/Main.scopes'
import TaMapPresenter from './ta_map_presenter'

const LOG = Logger.get('TheActingPresenter')

const storage = window.sessionStorage
const appStorage = AppStorage.singleton()
const service = TheActingService.singleton()

const CURRENCY_FORMATTER = new Intl.NumberFormat('pt-BR', { style: 'decimal', currency: 'BRL' })
const CK_FILTERS = 'ta-3d7712ff165a'
const CK_CRITERIA = 'ta-3d7712ff165b'

export class TheActingPresenter extends CubePresenter<MainPresenter, TheActingScope> {
    // :: Fields
    private readonly __headerBar: Presenter<HeaderScope, TheActingPresenter>
    private readonly __searchPresenter: SearchBoxPresenter
    private readonly __mapPresenter: TaMapPresenter
    private readonly __filterRemovalAction = new FilterRemovalAction()
    private readonly __confirmExportDataPresenter: ConfirmExportDataLimitPresenter

    private __working = false

    private __companyCursors: (unknown[] | undefined)[] = []
    private __professionalCursors: (unknown[] | undefined)[] = []
    private __cursorSignature = ''

    public constructor(app: MainPresenter) {
        super(app, new TheActingScope())
        this.__headerBar = new Presenter(this, new HeaderScope())
        this.__searchPresenter = new SearchBoxPresenter(app, this, this.__headerBar.scope.search)
        this.__searchPresenter.mode = 'ACTING'
        this.__searchPresenter.pageSize = 100
        this.__mapPresenter = new TaMapPresenter(this)
        this.__confirmExportDataPresenter = new ConfirmExportDataLimitPresenter(app, this.scope.exportDataLimitScope)
    }

    public override release() {
        if (this.app.scope.toolbar === this.__headerBar.scope) {
            this.app.scope.toolbar = undefined
        }
        this.__confirmExportDataPresenter.release()
        this.__mapPresenter.release()
        this.__searchPresenter.release()
        this.__headerBar.release()
        super.release()
        LOG.debug('Finalized')
    }

    public override async applyParameters(intent: FlipIntent, initialization: boolean): Promise<boolean> {
        if (!this.app.scope.theActingEnabled) {
            await this.app.flipToDefault()
            return false
        }

        const keys = new TheActingKeys(this.app, intent)

        this.app.scope.toolbar = this.__headerBar.scope
        keys.parentSlot(this.scope)

        if (initialization) {
            await this.__initialize(keys)
        } else {
            await this.__update(keys)
        }

        if (keys.last) {
            keys.dialogSlot(null)
        }

        return keys.allow
    }

    public override publishParameters(intent: FlipIntent): void {
        const keys = new TheActingKeys(this.app, intent)
        keys.selectedCard = this.scope.selectedCard
    }

    public onComplianceChanged(compliance: boolean) {
        this.__searchPresenter.onComplianceChanged(compliance)
    }

    public buildQuery(request: FetchRequest) {
        if (this.app.compliance) {
            request.compliant = true
        }

        if (!this.__headerBar.scope.onlyActives) {
            request.nonActives = true
        }

        {
            const ctx = new HandleSearchFilterAppliedCtx()
            request.filters = ctx.mergeFilters(request.filters ?? {}, ctx.currentFilters(this.scope.overlay))
            request.criteria = ctx.mergeCriteria(request.criteria ?? {}, ctx.currentCriteria(this.scope.overlay))

            request.filters && ctx.preserveTextsWithoutId(request.filters)
        }

        if (this.__headerBar.scope.onlySemVinculo) {
            request.criteria = request.criteria ?? {}
            request.criteria.pesSemVinculo = true
        }

        // completa dados de empresa
        request.company = request.company ?? {}

        const companySortBy: CompanySortModel[] =
            this.scope.companyCard.sortBy.length > 0 ? this.scope.companyCard.sortBy : [{ field: 'nome', sort: 'asc' }]

        if (lodash.isNil(request.company.offset) && this.scope.companyCard.page > 0) {
            request.company.offset = this.scope.companyCard.page * this.scope.companyCard.pageSize
        }

        if (lodash.isNil(request.company.limit)) {
            request.company.limit = this.scope.companyCard.pageSize
        }

        if (lodash.isNil(request.company.sort)) {
            const sortModel: CompanySortItem[] = []
            companySortBy.forEach((e) => sortModel.push([e.field, e.sort]))
            request.company.sort = sortModel
        }

        // completa dados de empresa

        request.professional = request.professional ?? {}

        const professionalSortBy: ProfessionalSortModel[] =
            this.scope.professionalCard.sortBy.length > 0
                ? this.scope.professionalCard.sortBy
                : [{ field: 'nome', sort: 'asc' }]

        if (lodash.isNil(request.professional.offset) && this.scope.professionalCard.page > 0) {
            request.professional.offset = this.scope.professionalCard.page * this.scope.professionalCard.pageSize
        }

        if (lodash.isNil(request.professional.limit)) {
            request.professional.limit = this.scope.professionalCard.pageSize
        }

        if (lodash.isNil(request.professional.sort)) {
            const sortModel: ProfessionalSortItem[] = []
            professionalSortBy.forEach((e) => sortModel.push([e.field, e.sort]))
            request.professional.sort = sortModel
        }

        if (lodash.isNil(request.professional.existsFilters)) {
            const existing = this.scope.professionalCard.existsFilters
            if (existing && Object.keys(existing).length > 0) {
                request.professional.existsFilters = { ...existing }
            }
        }

        this.__mapPresenter.prepareCriteria(request)

        this.__applyPaginationCursors(request)
    }

    private __applyPaginationCursors(request: FetchRequest) {
        const signature = JSON.stringify({
            filters: request.filters,
            criteria: request.criteria,
            compliant: request.compliant,
            nonActives: request.nonActives,
            companySort: request.company?.sort,
            companyLimit: request.company?.limit,
            professionalSort: request.professional?.sort,
            professionalLimit: request.professional?.limit,
            existsFilters: request.professional?.existsFilters
        })

        if (signature !== this.__cursorSignature) {
            this.__cursorSignature = signature
            this.__companyCursors.length = 0
            this.__professionalCursors.length = 0
        }

        applyCursorTo(request.company, this.__companyCursors)
        applyCursorTo(request.professional, this.__professionalCursors)
    }

    public async fetchData(refreshOverlayView: boolean) {
        this.__startWorking()
        try {
            const request: FetchRequest = {}
            if (refreshOverlayView) {
                await this.__doSearchFilterApplied(request)
            } else {
                await this.__fetchData(request)
            }
        } finally {
            this.__stopWorking()
        }
    }

    private async __initialize(keys: TheActingKeys) {
        this.__setSelectedCard(keys.selectedCard)

        this.__headerBar.scope.onOnlyActivesChange = this.action(this.__handleOnlyActivesChange)
        this.__headerBar.scope.onOnlySemVinculoChange = this.action(this.__handleOnlySemVinculoChange)
        this.__searchPresenter.initialize()
        this.__searchPresenter.onComplianceChanged(this.app.compliance)
        this.__searchPresenter.onSelected = () => {
            this.__searchPresenter.apply().catch(LOG.caught)
        }
        this.__searchPresenter.onApply = this.__handleSearchApply.bind(this)
        this.__searchPresenter.onSearch = this.__handleSearchFetch.bind(this)

        {
            const sectionUpdate = this.scope.overlay.updateAsRoot()
            this.scope.overlay.downloadEnabled = appStorage.theActingPrintEnabled
            this.scope.overlay.onFilterToggleClick = this.action(this.__handleFilterToggleClick)
            this.scope.overlay.onDownloadClick = this.action(this.__onDownloadClicked.bind(this, 'all'))

            this.scope.overlay.livre.update = sectionUpdate
            this.scope.overlay.empresa.update = sectionUpdate
            this.scope.overlay.cnes.update = sectionUpdate
            this.scope.overlay.socios.update = sectionUpdate
            this.scope.overlay.pessoa.update = sectionUpdate
            this.scope.overlay.pessoaAndSocio.update = sectionUpdate
            this.scope.overlay.operadoras.update = sectionUpdate
        }

        this.scope.companyCard.downloadEnabled = appStorage.theActingPrintEnabled
        this.scope.companyCard.onExpandClick = this.action(this.__setSelectedCard.bind(this, 'company'))
        this.scope.companyCard.onShrunkClick = this.action(this.__setSelectedCard.bind(this, 'none'))
        this.scope.companyCard.onDownloadClick = this.action(this.__onDownloadClicked.bind(this, 'company'))
        this.scope.companyCard.onPageChange = this.action(this.__handleCompanyPageChange)
        this.scope.companyCard.onPageSizeChange = this.action(this.__handleCompanyPageSizeChange)
        this.scope.companyCard.onRowClick = this.action(this.__handleCompanyRowClick)
        this.scope.companyCard.onSortModelChange = this.action(this.__handleCompanySortModelChange)
        this.scope.companyCard.updateAsRoot()

        this.scope.professionalCard.downloadEnabled = appStorage.theActingPrintEnabled
        this.scope.professionalCard.onExpandClick = this.action(this.__setSelectedCard.bind(this, 'professional'))
        this.scope.professionalCard.onShrunkClick = this.action(this.__setSelectedCard.bind(this, 'none'))
        this.scope.professionalCard.onDownloadClick = this.action(this.__onDownloadClicked.bind(this, 'professional'))
        this.scope.professionalCard.onPageChange = this.action(this.__handleProfessionalPageChange)
        this.scope.professionalCard.onPageSizeChange = this.action(this.__handleProfessionalPageSizeChange)
        this.scope.professionalCard.onRowClick = this.action(this.__handleProfessionalRowClick)
        this.scope.professionalCard.onDetailClick = this.action(this.__handleProfessionalDetailClick)
        this.scope.professionalDetail.onClose = this.action(this.__handleProfessionalDetailClose)
        this.scope.professionalDetail.onFilterByProfessional = this.action(this.__handleProfessionalDetailFilter)
        this.scope.professionalCard.onSortModelChange = this.action(this.__handleProfessionalSortModelChange)
        this.scope.professionalCard.onExistsFilterChange = this.action(this.__handleProfessionalExistsFilterChange)
        this.scope.professionalCard.onClearExistsFilters = this.action(this.__handleProfessionalClearExistsFilters)
        this.scope.professionalCard.updateAsRoot()

        await this.__mapPresenter.initialize()
        this.scope.exportDataLimitScope.onYesNoClicked = this.action(this.__onYesNoConfirmExport)
        this.scope.exportDataLimitScope.canExcludeAlreadyExported = true

        this.__confirmExportDataPresenter.initialize()

        this.scope.mapCard.onExpandClick = this.action(this.__setSelectedCard.bind(this, 'map'))
        this.scope.mapCard.onShrunkClick = this.action(this.__setSelectedCard.bind(this, 'none'))
        //this.scope.mapCard.onMapChanged = this.__handleMapChanged.bind(this)
        this.scope.mapCard.updateAsRoot()

        this.__filterRemovalAction.overlay = this.scope.overlay
        this.__filterRemovalAction.searchFilterAppliedListener = this.__handleSearchApply.bind(this)

        const request: FetchRequest = { filters: {}, criteria: {} }

        const jsonFilters = storage.getItem(CK_FILTERS)
        if (jsonFilters) {
            console.debug(jsonFilters)
            request.filters = JSON.parse(jsonFilters)
        }

        const jsonCriteria = storage.getItem(CK_CRITERIA)
        if (jsonCriteria) {
            request.criteria = JSON.parse(jsonCriteria)
        }

        this.__startWorking()
        this.__doSearchFilterApplied(request).finally(() => {
            this.__stopWorking()
        })

        LOG.debug('Initalized')
    }

    private async __update(keys: TheActingKeys) {
        if (this.scope.selectedCard !== keys.selectedCard) {
            this.__setSelectedCard(keys.selectedCard)
        }
    }

    private __startWorking() {
        this.scope.professionalCard.working = true
        this.scope.companyCard.working = true
        this.__working = true
    }

    private __stopWorking() {
        this.scope.professionalCard.working = false
        this.scope.companyCard.working = false
        this.__working = false
    }

    private __setScore(scope: ScoreCardScope, label: string, value: number) {
        scope.update = this.update
        scope.label = label
        const absValue = Math.abs(value)
        if (absValue >= 1_000_000 && absValue < 1_000_000_000) {
            scope.unit = 'Mi'
            value = value / 1_000_000
        } else if (absValue >= 1_000_000_000) {
            scope.unit = 'Bi'
            value = value / 1_000_000_000
        } else {
            scope.unit = ''
        }

        scope.value = CURRENCY_FORMATTER.format(value)
        scope.update()
    }

    private __populateCompanies(tableData: TablePage<CompanyRow>) {
        const pageSize = Math.max(tableData.limit, 1)
        const pageIndex = tableData.offset / pageSize
        const companyCardScope = this.scope.companyCard
        companyCardScope.total = tableData.total
        companyCardScope.page = pageIndex
        companyCardScope.pageSize = pageSize
        companyCardScope.rows = tableData.rows
    }

    private __populateProfessionals(tableData: TablePage<ProfessionalRow>) {
        const pageSize = Math.max(tableData.limit, 1)
        const pageIndex = tableData.offset / pageSize
        const professionalCardScope = this.scope.professionalCard
        professionalCardScope.total = tableData.total
        professionalCardScope.page = pageIndex
        professionalCardScope.pageSize = pageSize
        professionalCardScope.rows = tableData.rows
    }

    private async __setSelectedCard(cardName: string | undefined) {
        switch (cardName ?? 'none') {
            case 'company':
                this.scope.selectedCard = 'company'
                break
            case 'professional':
                this.scope.selectedCard = 'professional'
                break
            case 'map':
                this.scope.selectedCard = 'map'
                break
            default:
                this.scope.selectedCard = 'none'
        }

        this.scope.companyCard.expanded = this.scope.selectedCard === 'company'
        this.scope.professionalCard.expanded = this.scope.selectedCard === 'professional'
        this.scope.mapCard.expanded = this.scope.selectedCard === 'map'
    }

    async __onDownloadClicked(mode: 'company' | 'professional' | 'all') {
        this.scope.downloadMode = mode

        const ctx = new HandleSearchFilterAppliedCtx()
        const filters = ctx.currentFilters(this.scope.overlay)
        ctx.preserveTextsWithoutId(filters)

        const criteria = ctx.currentCriteria(this.scope.overlay)

        const req: FetchRequest = {
            compliant: this.app.compliance,
            company: mode === 'all' || mode === 'company' ? {} : undefined,
            professional: mode === 'all' || mode === 'professional' ? {} : undefined,
            filters,
            criteria
        }
        this.__mapPresenter.prepareCriteria(req)

        this.scope.exportDataLimitScope.isChecking = true
        this.scope.update()
        try {
            const limitExportData = await service.checkLimitExportDataDownload(req)
            this.scope.exportDataLimitScope.onCheckLimitExportData(limitExportData)
        } finally {
            this.scope.exportDataLimitScope.isChecking = false
        }
    }

    protected async __onYesNoConfirmExport(yes: boolean) {
        LOG.debug(`__onYesNoConfirmExport: yes=${yes}`)
        if (yes) {
            await this.__download(this.scope.downloadMode)
        }
        this.scope.exportDataLimitScope.open = false
        this.scope.update()
    }

    private async __download(mode: 'company' | 'professional' | 'all') {
        if (this.__working) {
            return
        }
        this.scope.overlay.downloading = true
        this.scope.exportDataLimitScope.isDownloading = true
        this.__startWorking()
        try {
            const ctx = new HandleSearchFilterAppliedCtx()
            const filters = ctx.currentFilters(this.scope.overlay)
            ctx.preserveTextsWithoutId(filters)

            const criteria = ctx.currentCriteria(this.scope.overlay)

            const req: FetchRequest = {
                compliant: this.app.compliance,
                company: mode === 'all' || mode === 'company' ? {} : undefined,
                professional: mode === 'all' || mode === 'professional' ? {} : undefined,
                filters,
                criteria,
                qttRecordsToDownload: this.scope.exportDataLimitScope.qttExportRecords || 0,
                excludeCompanyDocIds: excludedExportDocIdsByType(this.scope.exportDataLimitScope, 'acting_company'),
                excludeProfessionalDocIds: excludedExportDocIdsByType(
                    this.scope.exportDataLimitScope,
                    'acting_professional'
                )
            }
            this.__mapPresenter.prepareCriteria(req)

            const blob = await service.download(req)
            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([blob]))
            const link = document.createElement('a')
            link.href = url
            link.setAttribute('download', `the-acting-${mode}.xlsx`)

            // Append to html link element page
            document.body.appendChild(link)

            // Start download
            link.click()

            // Clean up and remove the link
            link.parentNode?.removeChild(link)
        } finally {
            this.scope.overlay.downloading = false
            this.scope.exportDataLimitScope.isDownloading = false
            this.__stopWorking()
        }
    }

    private async __fetchData(request: FetchRequest) {
        this.__startWorking()
        try {
            this.buildQuery(request)

            const response = await service.fetch(request)
            await this.__mapPresenter.fetch(request).catch(LOG.error)

            if (response.scores) {
                this.__setScore(this.scope.profissionalScoreCard, 'Profissionais', response.scores.professionals)
                this.__setScore(this.scope.companyScoreCard, 'Empresas', response.scores.companies)
                this.__setScore(this.scope.revenueScoreCard, 'Faturamento Est.', response.scores.revenue)
                this.__setScore(this.scope.healthCoScoreCard, 'Estabelecimentos', response.scores.healthCo)
            }

            response.professionalData && this.__populateProfessionals(response.professionalData)
            response.companyData && this.__populateCompanies(response.companyData)

            if (response.companyData && response.companyData.limit > 0) {
                this.scope.companyCard.page = (response.companyData.offset / response.companyData.limit) | 0
                this.scope.companyCard.pageSize = response.companyData.limit
            } else {
                this.scope.companyCard.page = 0
            }

            if (response.professionalData && response.professionalData.limit > 0) {
                this.scope.professionalCard.page =
                    (response.professionalData.offset / response.professionalData.limit) | 0
                this.scope.professionalCard.pageSize = response.professionalData.limit
            } else {
                this.scope.professionalCard.page = 0
            }

            if (response.companyData) {
                this.__companyCursors[this.scope.companyCard.page + 1] = response.companyData.nextCursor
            }

            if (response.professionalData) {
                this.__professionalCursors[this.scope.professionalCard.page + 1] = response.professionalData.nextCursor
            }

            if (request.company?.sort) {
                this.scope.companyCard.sortBy.length = 0
                for (const [field, sort] of request.company.sort) {
                    this.scope.companyCard.sortBy.push({ field, sort })
                }
                this.scope.companyCard.update()
            }

            if (request.professional?.sort) {
                this.scope.professionalCard.sortBy.length = 0
                for (const [field, sort] of request.professional.sort) {
                    this.scope.professionalCard.sortBy.push({ field, sort })
                }
                this.scope.professionalCard.update()
            }

            storage.setItem(CK_FILTERS, JSON.stringify(request.filters))
            storage.setItem(CK_CRITERIA, JSON.stringify(request.criteria))
        } catch (exn) {
            LOG.error('Falha ao consultar', exn)
            const message = exn instanceof Error ? exn.message : 'Erro desconhecido'
            this.app.alert('error', 'Consulta não concluída', message)
        } finally {
            this.__stopWorking()
        }
    }

    private async __handleOnlyActivesChange(checked: boolean) {
        this.__headerBar.scope.onlyActives = checked

        this.__startWorking()
        this.__doSearchFilterApplied({}).finally(() => {
            this.__stopWorking()
        })
    }

    private async __handleOnlySemVinculoChange(checked: boolean) {
        this.__headerBar.scope.onlySemVinculo = checked
        this.scope.companyCard.page = 0
        this.scope.professionalCard.page = 0

        this.__startWorking()
        this.__doSearchFilterApplied({}).finally(() => {
            this.__stopWorking()
        })
    }

    private async __handleFilterToggleClick() {
        this.scope.overlay.expanded = !this.scope.overlay.expanded
    }

    private async __handleProfessionalPageChange(newPage: number) {
        if (this.__working) return
        this.scope.professionalCard.page = newPage
        await this.__doSearchFilterApplied({})
    }

    private async __handleProfessionalPageSizeChange(newPageSize: number) {
        if (this.__working) return
        this.scope.professionalCard.page = 0
        this.scope.professionalCard.pageSize = newPageSize
        await this.__doSearchFilterApplied({})
    }

    private async __handleProfessionalRowClick(row: ProfessionalRow) {
        if (this.__working) return
        this.scope.professionalCard.page = 0
        await this.__doSearchFilterApplied({
            filters: {
                pessoas: [row.id]
            }
        })
    }

    private async __handleProfessionalDetailClick(row: ProfessionalRow) {
        const detail = this.scope.professionalDetail

        detail.open = true
        detail.loading = true
        detail.error = ''
        detail.profissional = undefined
        detail.vinculos = []
        detail.semVinculo = false
        detail.updateAsRoot()

        try {
            const data = await service.fetchProfessionalDetail(row.id, !!this.app.compliance)
            if (!detail.open) return
            detail.profissional = data.profissional
            detail.vinculos = data.vinculos ?? []
            detail.semVinculo = !!data.semVinculo
        } catch (e) {
            LOG.error('Falha ao carregar a ficha do profissional', e)
            detail.error = 'Não foi possível carregar a ficha deste profissional.'
        } finally {
            detail.loading = false
            detail.updateAsRoot()
        }
    }

    private async __handleProfessionalDetailClose() {
        const detail = this.scope.professionalDetail
        detail.open = false
        detail.profissional = undefined
        detail.vinculos = []
        detail.updateAsRoot()
    }

    private async __handleProfessionalDetailFilter() {
        const detail = this.scope.professionalDetail
        const id = detail.profissional?.id
        await this.__handleProfessionalDetailClose()
        if (!id) return
        if (this.__working) return

        this.scope.professionalCard.page = 0
        await this.__doSearchFilterApplied({ filters: { pessoas: [id] } })
    }

    private async __handleCompanyPageChange(newPage: number) {
        if (this.__working) return
        this.scope.companyCard.page = newPage
        await this.__doSearchFilterApplied({})
    }

    private async __handleCompanyPageSizeChange(newPageSize: number) {
        this.scope.companyCard.page = 0
        this.scope.companyCard.pageSize = newPageSize
        await this.__doSearchFilterApplied({})
    }

    private async __handleCompanyRowClick(row: CompanyRow) {
        if (this.__working) return
        this.scope.companyCard.page = 0

        if (row.semVinculo) {
            await this.__handleOnlySemVinculoChange(true)
            return
        }

        let filterCount = 0
        const filters: TextualSearchRequest['filters'] = {}
        if (row.cnes) {
            filters.estabelecimentos = [row.cnes]
            filterCount++
        } else if (row.cnpj) {
            filters.cnpjs = [row.cnpj]
            filterCount++
        }

        if (filterCount > 0) {
            await this.__doSearchFilterApplied({ filters })
        }
    }

    private async __handleCompanySortModelChange(sortModel: CompanySortModel[]) {
        const newSortModel: CompanySortItem[] = []

        if (this.scope.keyboard.ctrlKey) {
            this.scope.companyCard.sortBy.forEach(({ field, sort }) => newSortModel.push([field, sort]))
        }
        sortModel.forEach(({ field, sort }) => newSortModel.push([field, sort]))

        await this.__doSearchFilterApplied({
            company: {
                offset: 0,
                sort: newSortModel
            }
        })
    }

    private async __handleProfessionalSortModelChange(sortModel: ProfessionalSortModel[]) {
        const newSortModel: ProfessionalSortItem[] = []
        if (this.scope.keyboard.ctrlKey) {
            this.scope.professionalCard.sortBy.forEach(({ field, sort }) => newSortModel.push([field, sort]))
        }

        sortModel.forEach(({ field, sort }) => newSortModel.push([field, sort]))

        await this.__doSearchFilterApplied({
            professional: {
                offset: 0,
                sort: newSortModel
            }
        })
    }

    private async __handleProfessionalExistsFilterChange(payload: {
        field: ProfessionalExistsFilterField
        mode: ProfessionalExistsFilterMode
    }) {
        if (this.__working) return
        const next = { ...this.scope.professionalCard.existsFilters, [payload.field]: payload.mode }
        this.scope.professionalCard.existsFilters = next
        this.scope.professionalCard.page = 0
        await this.__doSearchFilterApplied({
            professional: {
                offset: 0,
                existsFilters: next
            }
        })
    }

    private async __handleProfessionalClearExistsFilters() {
        if (this.__working) return
        if (Object.keys(this.scope.professionalCard.existsFilters).length === 0) return
        this.scope.professionalCard.existsFilters = {}
        this.scope.professionalCard.page = 0
        await this.__doSearchFilterApplied({
            professional: {
                offset: 0,
                existsFilters: {}
            }
        })
    }

    private async __handleSearchFetch(req: TextualSearchRequest) {
        req.nonActives = !this.__headerBar.scope.onlyActives
        req.compliance = this.app.compliance
        return await service.filterSearch(req)
    }

    private async __handleSearchApply(newRequest: TextualSearchRequest) {
        if (this.__working) {
            this.app.alert('info', 'Aviso', 'Existe uma consulta em andamento.')
            return
        }

        // console.debug('__handleSearchApply', JSON.stringify(newRequest))

        await this.__doSearchFilterApplied({
            company: {
                offset: 0
            },
            professional: {
                offset: 0
            },
            filters: newRequest.filters,
            criteria: newRequest.criteria
        })
    }

    private async __doSearchFilterApplied(newRequest: FetchRequest, runDry = false) {
        this.__searchPresenter.clearSelections()
        this.__searchPresenter.clearValue()

        const ctx = new HandleSearchFilterAppliedCtx()
        const filters = ctx.mergeFilters(newRequest.filters ?? {}, ctx.currentFilters(this.scope.overlay))
        const criteria = ctx.mergeCriteria(newRequest.criteria ?? {}, ctx.currentCriteria(this.scope.overlay))

        const { filters: describedFilters = {} } = await this.__searchPresenter.describeRequest({
            filters: ctx.filterToOnlyIds(filters)
        })

        ctx.appendTextsIfNotPresent(describedFilters, filters)

        const onRemoveListener = this.action(this.__filterRemovalAction.run.bind(this.__filterRemovalAction))

        ctx.applyFilterOnOverlay(this.scope.overlay, describedFilters, onRemoveListener)
        ctx.applyCriteriaOnOverlay(this.scope.overlay, criteria, onRemoveListener)

        if (!runDry) {
            await this.__fetchData(newRequest)
        }

        this.scope.overlay.update()
    }
}

function applyCursorTo(
    target: { offset?: number; limit?: number; search_after?: unknown[] } | undefined,
    cursors: (unknown[] | undefined)[]
) {
    if (!target || !lodash.isNil(target.search_after)) {
        return
    }

    const limit = target.limit ?? 0
    const page = limit > 0 ? Math.floor((target.offset ?? 0) / limit) : 0
    if (page <= 0) {
        return
    }

    const cursor = cursors[page]
    if (cursor) {
        target.search_after = cursor
    }
}

class FilterRemovalAction {
    private __removingFilter = false
    private __pendingFilterToBeRemovedArray: FilterEntryScope[] = []

    overlay!: OverlayScope
    searchFilterAppliedListener!: (newRequest: TextualSearchRequest) => Promise<void>

    async run(scope: FilterEntryScope) {
        try {
            this.__pendingFilterToBeRemovedArray.push(scope)
            if (this.__removingFilter) {
                return
            }

            this.__removingFilter = true
            while (this.__pendingFilterToBeRemovedArray.length > 0) {
                const scopeArray = [...this.__pendingFilterToBeRemovedArray]
                this.__pendingFilterToBeRemovedArray.length = 0
                await this.doRemoveFilters(scopeArray)
            }
        } finally {
            this.__removingFilter = false
        }
    }

    async doRemoveFilters(scopeArray: FilterEntryScope[]) {
        const { overlay } = this

        const doUpdate = async () => {
            await this.searchFilterAppliedListener({})
        }

        const filters = [
            ...overlay.livre.getAllFilters(),
            ...overlay.empresa.getAllFilters(),
            ...overlay.cnes.getAllFilters(),
            ...overlay.socios.getAllFilters(),
            ...overlay.pessoa.getAllFilters(),
            ...overlay.pessoaAndSocio.getAllFilters(),
            ...overlay.operadoras.getAllFilters()
        ]

        let numRemoved = 0

        for (const scope of scopeArray) {
            const removeCriteria = (item: FilterEntryScope) => item === scope

            for (const filterArrayEntry of filters) {
                if (filterArrayEntry.removeByCriteria(removeCriteria)) {
                    numRemoved++
                    break
                }
            }
        }

        if (numRemoved > 0) {
            await doUpdate()
        }
    }
}
